import React, { createContext, useState, useContext, useEffect } from "react";
import {
  loginRetailOne,
  getCurrentRetailOne,
  logoutRetailOne,
  loginAdmin,
  getCurrentAdmin,
  logoutAdmin,
  verifyRetailOneOTP,
  resendRetailOneOTP,
} from "../services/authService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [val, setVal] = useState(null);
  const [features, setFeatures] = useState([]);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    getCurrentRetailOne().then((result) => {
      if (result.success) {
        setUser(result.user);
        setUserType("retailone");
        // Restore val from storage
        const storedVal = localStorage.getItem("sba_val");
        if (storedVal) {
          setVal(storedVal);
        }
        // Restore features and permissions from storage
        const storedFeatures = localStorage.getItem("sba_features");
        const storedPermissions = localStorage.getItem("sba_permissions");
        if (storedFeatures) {
          try {
            setFeatures(JSON.parse(storedFeatures));
          } catch (e) {
            console.error("Failed to parse stored features", e);
          }
        }
        if (storedPermissions) {
          try {
            setPermissions(JSON.parse(storedPermissions));
          } catch (e) {
            console.error("Failed to parse stored permissions", e);
          }
        }
        setLoading(false);
        return;
      }
      getCurrentAdmin().then((result2) => {
        if (result2.success) {
          setUser(result2.user);
          setUserType("admin");
        }
        setLoading(false);
      });
    });
  }, []);

  const loginRetailOneUser = async (email, password) => {
    const result = await loginRetailOne(email, password);
    if (result.success) {
      setUser(result.user);
      setUserType("retailone");

      // Store val if present
      if (result.val) {
        setVal(result.val);
        localStorage.setItem("sba_val", result.val);
      } else {
        setVal(null);
        localStorage.removeItem("sba_val");
      }

      // Store features
      if (result.features && Array.isArray(result.features)) {
        setFeatures(result.features);
        localStorage.setItem("sba_features", JSON.stringify(result.features));
      } else {
        setFeatures([]);
        localStorage.removeItem("sba_features");
      }

      // Store permissions
      if (result.permissions && Array.isArray(result.permissions)) {
        setPermissions(result.permissions);
        localStorage.setItem(
          "sba_permissions",
          JSON.stringify(result.permissions),
        );
      } else {
        setPermissions([]);
        localStorage.removeItem("sba_permissions");
      }
    }
    return result;
  };

  const loginAdminUser = async (email, password) => {
    const result = await loginAdmin(email, password);
    if (result.success) {
      setUser(result.user);
      setUserType("admin");
      if (result.val) {
        setVal(result.val);
        localStorage.setItem("sba_val", result.val);
      } else {
        setVal(null);
        localStorage.removeItem("sba_val");
      }
      window.location.href = "/admin-dashboard";
    }
    return result;
  };

  const logout = async () => {
    if (userType === "retailone") {
      await logoutRetailOne();
    } else if (userType === "admin") {
      await logoutAdmin();
    }
    setUser(null);
    setUserType(null);
    setVal(null);
    setFeatures([]);
    setPermissions([]);
    localStorage.removeItem("sba_val");
    localStorage.removeItem("sba_features");
    localStorage.removeItem("sba_permissions");
    window.location.href = "/";
  };

  const verifyRetailOneOTPFunction = async (email, otp, session_id) => {
    // console.log("🔵 verifyRetailOneOTPFunction called");
    const result = await verifyRetailOneOTP(email, otp, session_id);
    if (result.success) {
      setUser(result.user);
      setUserType("retailone");

      //  Store val
      if (result.val) {
        setVal(result.val);
        localStorage.setItem("sba_val", result.val);
      } else {
        setVal(null);
        localStorage.removeItem("sba_val");
      }

      // Store features (same as login)
      if (result.features && Array.isArray(result.features)) {
        setFeatures(result.features);
        localStorage.setItem("sba_features", JSON.stringify(result.features));
      } else {
        setFeatures([]);
        localStorage.removeItem("sba_features");
      }

      // Store permissions (same as login)
      if (result.permissions && Array.isArray(result.permissions)) {
        setPermissions(result.permissions);
        localStorage.setItem(
          "sba_permissions",
          JSON.stringify(result.permissions),
        );
      } else {
        setPermissions([]);
        localStorage.removeItem("sba_permissions");
      }
    }
    return result;
  };

  const resendRetailOneOTPFunction = async (email, session_id) => {
    // console.log("🔵 resendRetailOneOTPFunction called");
    return await resendRetailOneOTP(email, session_id);
  };

  // ============================================================
  // HELPER FUNCTIONS
  // ============================================================
  const hasFeature = (featureCode) => {
    return features.includes(featureCode);
  };

  const hasPermission = (permissionCode) => {
    return permissions.includes(permissionCode);
  };

  const canAccess = (featureCode, permissionCode) => {
    return hasFeature(featureCode) && hasPermission(permissionCode);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userType,
        loading,
        val,
        features,
        permissions,
        loginRetailOne: loginRetailOneUser,
        loginAdmin: loginAdminUser,
        logout,
        verifyRetailOneOTP: verifyRetailOneOTPFunction,
        resendRetailOneOTP: resendRetailOneOTPFunction,
        isAuthenticated: !!user,
        isRetailOne: userType === "retailone",
        isAdmin: userType === "admin",
        hasAccess: !!val,
        // Helper functions
        hasFeature,
        hasPermission,
        canAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
